// Backend API Service - Connects to Django backend

// In-flight request deduplication + short-lived TTL result cache.
// Prevents identical calls fired concurrently (React StrictMode, multiple mounted
// components) AND sequential calls within the TTL window from hitting the network.
const _inFlightRequests = new Map(); // key → Promise
const _resultCache = new Map();      // key → { result, expiresAt }
const _RESULT_CACHE_TTL = 5000;      // 5 seconds
const _AUTH_ME_CACHE_TTL = 15000;     // 15 seconds
const _NAV_MODULES_CACHE_TTL = 15000; // 15 seconds
const _SITES_CACHE_TTL = 20000; // 20 seconds
const _MAX_RETRIES = 2;

function _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function _isIdempotentMethod(method) {
    const m = String(method || 'GET').toUpperCase();
    return m === 'GET' || m === 'HEAD' || m === 'OPTIONS';
}

function _shouldRetryStatus(status) {
    // Do not auto-retry 429 here; immediate retries amplify rate-limit hits.
    return status === 408 || (status >= 500 && status <= 599);
}

function _extractSiteFromPath(pathname) {
    const path = String(pathname || '');
    if (!path) return '';
    const match = path.match(/\/(navigate|entry|upload|dashboard|simulation|profile)\/([^/?#]+)/i);
    return match?.[2] ? String(match[2]).trim().toUpperCase() : '';
}

function _dedupRequest(key, requestFn) {
    // Return cached result if still fresh
    const cached = _resultCache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
        return Promise.resolve(cached.result);
    }
    // Return in-flight promise if a request is already running
    if (_inFlightRequests.has(key)) {
        return _inFlightRequests.get(key);
    }
    const promise = requestFn().then(
        result => {
            _inFlightRequests.delete(key);
            _resultCache.set(key, { result, expiresAt: Date.now() + _RESULT_CACHE_TTL });
            return result;
        },
        err => {
            _inFlightRequests.delete(key);
            throw err;
        }
    );
    _inFlightRequests.set(key, promise);
    return promise;
}

function _dedupRequestWithTtl(key, ttlMs, requestFn) {
    const ttl = Number.isFinite(Number(ttlMs)) && Number(ttlMs) > 0 ? Number(ttlMs) : _RESULT_CACHE_TTL;
    const cached = _resultCache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
        return Promise.resolve(cached.result);
    }
    if (_inFlightRequests.has(key)) {
        return _inFlightRequests.get(key);
    }
    const promise = requestFn().then(
        result => {
            _inFlightRequests.delete(key);
            _resultCache.set(key, { result, expiresAt: Date.now() + ttl });
            return result;
        },
        err => {
            _inFlightRequests.delete(key);
            throw err;
        }
    );
    _inFlightRequests.set(key, promise);
    return promise;
}

class BackendApiService {
    constructor() {
        const envBase = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').trim();
        this.baseURL = envBase.replace(/\/$/, '');
        this.token = this.normalizeToken(sessionStorage.getItem('access_token'));
        this.refreshTokenMemory = null;
        this.refreshPromise = null;
        this.migrateLegacyStoredRefreshToken();
    }

    migrateLegacyStoredRefreshToken() {
        const stored = this.normalizeToken(sessionStorage.getItem('refresh_token'));
        if (stored) {
            this.refreshTokenMemory = stored;
        }
        localStorage.removeItem('refresh_token');
    }

    setToken(token) {
        const normalized = this.normalizeToken(token);
        this.token = normalized;
        if (normalized) {
            sessionStorage.setItem('access_token', normalized);
        } else {
            sessionStorage.removeItem('access_token');
        }
    }

    setRefreshToken(token) {
        const normalized = this.normalizeToken(token);
        if (normalized) {
            this.refreshTokenMemory = normalized;
            sessionStorage.setItem('refresh_token', normalized);
        } else {
            this.refreshTokenMemory = null;
            sessionStorage.removeItem('refresh_token');
        }
        localStorage.removeItem('refresh_token');
    }

    clearAuthState() {
        this.token = null;
        this.refreshTokenMemory = null;
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        localStorage.removeItem('refresh_token');
    }

    async refreshAccessToken() {
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = (async () => {
            const refreshToken = this.refreshTokenMemory;

            try {
                const response = await fetch(`${this.baseURL}/auth/token/refresh/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: refreshToken ? JSON.stringify({ refresh: refreshToken }) : JSON.stringify({}),
                });

                const text = await response.text();
                const data = text ? JSON.parse(text) : {};

                if (!response.ok || !data?.success) {
                    this.clearAuthState();
                    return null;
                }

                const accessToken =
                    this.normalizeToken(data?.data?.access_token) ||
                    this.normalizeToken(data?.data?.token) ||
                    this.normalizeToken(data?.access_token) ||
                    this.normalizeToken(data?.token);
                const nextRefreshToken =
                    this.normalizeToken(data?.data?.refresh_token) ||
                    this.normalizeToken(data?.refresh_token) ||
                    refreshToken;

                if (!accessToken) {
                    this.clearAuthState();
                    return null;
                }

                this.setToken(accessToken);
                this.setRefreshToken(nextRefreshToken);
                return accessToken;
            } catch (error) {
                this.clearAuthState();
                return null;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    normalizeToken(token) {
        if (!token) return null;
        const text = String(token).trim();
        if (!text || ['null', 'undefined', 'none', 'nan'].includes(text.toLowerCase())) return null;
        return text.replace(/^Bearer\s+/i, '').trim();
    }

    normalizeId(value) {
        if (value === undefined || value === null) return null;
        const text = String(value).trim();
        if (!text || ['null', 'undefined', 'none', 'nan'].includes(text.toLowerCase())) return null;
        const parsed = Number(text);
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return String(Math.trunc(parsed));
    }

    normalizeText(value) {
        if (value === undefined || value === null) return null;
        const text = String(value).trim();
        if (!text || ['null', 'undefined', 'none', 'nan'].includes(text.toLowerCase())) return null;
        return text;
    }

    decodeJwtPayload(token) {
        try {
            const normalized = this.normalizeToken(token);
            if (!normalized) return null;
            const parts = normalized.split('.');
            if (parts.length < 2) return null;
            const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + (4 - (base64.length % 4 || 4)) % 4, '=');
            const json = atob(padded);
            return JSON.parse(json);
        } catch {
            return null;
        }
    }

    isTokenExpiredOrNearExpiry(token, leewaySeconds = 30) {
        const payload = this.decodeJwtPayload(token);
        if (!payload || !payload.exp) return true;
        const expiresAtMs = Number(payload.exp) * 1000;
        if (!Number.isFinite(expiresAtMs)) return true;
        return Date.now() >= (expiresAtMs - (leewaySeconds * 1000));
    }

    async ensureValidAccessToken() {
        let token = this.normalizeToken(sessionStorage.getItem('access_token') || this.token);
        const hasRefreshToken = !!this.refreshTokenMemory;

        // If token exists and is healthy, use it directly.
        if (token && !this.isTokenExpiredOrNearExpiry(token)) {
            if (token !== this.token) this.token = token;
            return token;
        }

        // If token is missing/expired and refresh token is available, refresh before request.
        if (hasRefreshToken) {
            const refreshed = await this.refreshAccessToken();
            if (refreshed) return refreshed;
        }

        // Fall back to current token only when it exists and still appears usable.
        if (token && !this.isTokenExpiredOrNearExpiry(token, 0)) {
            return token;
        }

        return null;
    }

    getActiveSiteContext() {
        let routeSite = '';
        if (typeof window !== 'undefined') {
            routeSite = _extractSiteFromPath(window.location?.pathname || '');
        }

        const storedSiteCode =
            this.normalizeText(sessionStorage.getItem('tenant')) ||
            this.normalizeText(localStorage.getItem('currentSite')) ||
            '';

        const siteCode = this.normalizeText(routeSite || storedSiteCode) || '';

        if (siteCode) {
            sessionStorage.setItem('tenant', siteCode);
            localStorage.setItem('currentSite', siteCode);
        }

        const activeSiteId =
            this.normalizeId(sessionStorage.getItem('site_id')) ||
            this.normalizeId(localStorage.getItem('site_id')) ||
            '';

        return { siteCode, siteId: activeSiteId };
    }

    // Helper method to make API requests
    async makeRequest(endpoint, options = {}) {
        const requestOptions = { ...options };
        const retryAttempt = !!requestOptions._retry;
        const retryCount = Number(requestOptions._retryCount || 0);
        delete requestOptions._retry;
        delete requestOptions._retryCount;

        // Always get a valid token before protected requests to avoid first-hit 401 storms.
        let token = this.normalizeToken(sessionStorage.getItem('access_token') || this.token);
        if (token && token !== this.token) {
            this.token = token;
        }

        const activeSiteContext = this.getActiveSiteContext();
        const activeSiteCode = activeSiteContext.siteCode;
        const activeSiteId = activeSiteContext.siteId;
        const isAuthEndpoint =
            endpoint.startsWith('/auth/login/') ||
            endpoint.startsWith('/auth/register/') ||
            endpoint.startsWith('/auth/forgot-password/') ||
            endpoint.startsWith('/auth/reset-password/') ||
            endpoint.startsWith('/auth/verify-otp/') ||
            endpoint.startsWith('/auth/resend-otp/') ||
            endpoint.startsWith('/auth/check-username/') ||
            endpoint.startsWith('/auth/mfa/verify-login/') ||
            endpoint.startsWith('/auth/companies/active/') ||
            endpoint.startsWith('/data-deletion-request/') ||
            endpoint.startsWith('/auth/data-deletion-request/');

        const canAttemptRefresh = !retryAttempt && !endpoint.startsWith('/auth/token/refresh/') && !endpoint.startsWith('/auth/logout/');

        if (!isAuthEndpoint && canAttemptRefresh) {
            const ensuredToken = await this.ensureValidAccessToken();
            token = this.normalizeToken(ensuredToken || token);
            if (token && token !== this.token) {
                this.token = token;
            }
        }

        const url = `${this.baseURL}${endpoint}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
                ...(token && !isAuthEndpoint && activeSiteCode && { 'X-Site-Code': activeSiteCode }),
                ...(token && !isAuthEndpoint && activeSiteId && { 'X-Site-Id': String(activeSiteId) }),
                ...requestOptions.headers,
            },
            ...requestOptions,
        };
        
        // Add credentials for cookie-based auth and CSRF token
        config.credentials = 'include';
        
        // For state-changing requests, add CSRF token
        if (config.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())) {
            const csrfToken = this.getCSRFToken();
            if (csrfToken) {
                config.headers['X-CSRFToken'] = csrfToken;
            }
        }

        try {
            const response = await fetch(url, config);

            // Handle network errors
            if (!response) {
                const connError = new Error('SERVER_NOT_AVAILABLE');
                connError.code = 'CONNECTION_REFUSED';
                throw connError;
            }

            // Try to parse JSON, but handle cases where response might not be JSON
            let data;
            try {
                const text = await response.text();
                data = text ? JSON.parse(text) : {};
            } catch (parseError) {
                const connError = new Error('SERVER_NOT_AVAILABLE');
                connError.code = 'CONNECTION_REFUSED';
                throw connError;
            }

            if (!response.ok) {
                // Construct a more detailed error message
                let errorMessage = data.error || data.message || data.detail || 'Request failed';

                // Include detail if available and different from message
                if (data.detail && data.detail !== errorMessage) {
                    errorMessage = `${errorMessage}: ${data.detail}`;
                }

                // Include validation errors if available
                if (data.errors && typeof data.errors === 'object') {
                    const errorFields = Object.keys(data.errors);
                    if (errorFields.length > 0) {
                        // Prioritize non_field_errors for better user experience
                        if (data.errors.non_field_errors && Array.isArray(data.errors.non_field_errors)) {
                            errorMessage = data.errors.non_field_errors.join(', ');
                        } else {
                            const errorList = errorFields.map(field => {
                                const fieldErrors = Array.isArray(data.errors[field])
                                    ? data.errors[field].join(', ')
                                    : String(data.errors[field]);
                                return field === 'non_field_errors' ? fieldErrors : `${field}: ${fieldErrors}`;
                            }).join('; ');
                            errorMessage = errorMessage !== 'Request failed'
                                ? `${errorMessage} (${errorList})`
                                : errorList;
                        }
                    }
                }

                if (response.status === 401 && canAttemptRefresh) {
                    const refreshedToken = await this.refreshAccessToken();
                    if (refreshedToken) {
                        return this.makeRequest(endpoint, {
                            ...requestOptions,
                            _retry: true,
                            _retryCount: retryCount,
                        });
                    }
                }

                if (
                    retryCount < _MAX_RETRIES &&
                    _isIdempotentMethod(config.method) &&
                    _shouldRetryStatus(response.status)
                ) {
                    const backoffMs = 300 * (2 ** retryCount);
                    await _sleep(backoffMs);
                    return this.makeRequest(endpoint, {
                        ...requestOptions,
                        _retry: retryAttempt,
                        _retryCount: retryCount + 1,
                    });
                }

                const error = new Error(errorMessage);
                error.response = data;
                error.status = response.status;

                // Clear stale token immediately so subsequent calls do not keep failing with the same invalid JWT.
                if (
                    response.status === 401 &&
                    String(errorMessage || '').toLowerCase().includes('token') &&
                    String(errorMessage || '').toLowerCase().includes('valid')
                ) {
                    this.token = null;
                    sessionStorage.removeItem('access_token');
                }
                throw error;
            }

            return data;
        } catch (error) {
            // Handle connection errors - mark them so they can be handled gracefully
            if (error.message.includes('Failed to fetch') ||
                error.message.includes('ConnectionRefusedError') ||
                error.name === 'TypeError' ||
                error.message === 'SERVER_NOT_AVAILABLE' ||
                error.code === 'CONNECTION_REFUSED') {
                if (retryCount < _MAX_RETRIES && _isIdempotentMethod(requestOptions.method)) {
                    const backoffMs = 300 * (2 ** retryCount);
                    await _sleep(backoffMs);
                    return this.makeRequest(endpoint, {
                        ...requestOptions,
                        _retry: retryAttempt,
                        _retryCount: retryCount + 1,
                    });
                }
                const connError = new Error('SERVER_NOT_AVAILABLE');
                connError.code = 'CONNECTION_REFUSED';
                throw connError;
            }
            // Only log actual API errors in development (not connection issues)
            if (process.env.NODE_ENV === 'development' && error.code !== 'CONNECTION_REFUSED') {
                console.error('API Request Error:', error.message);
            }
            throw error;
        }
    }

    // Public request method (wrapper around makeRequest for API service compatibility)
    async request(endpoint, options = {}) {
        return this.makeRequest(endpoint, options);
    }

    // Authentication - Instagram-like login with username only
    async login(username, password, siteName = null, siteId = null, companyCode = null) {
        try {
            const loginData = { username, password };

            // Add company code
            if (companyCode) {
                loginData.company_code = companyCode;
            }

            // Only include site data if it's provided and not null/undefined/empty
            if (siteName && siteName !== 'not specified' && siteName.trim() !== '') {
                loginData.site_name = siteName.trim();
            }
            if (siteId && siteId !== 'not specified' && siteId !== '') {
                // Ensure siteId is a number
                const siteIdNum = parseInt(siteId);
                if (!isNaN(siteIdNum)) {
                    loginData.site_id = siteIdNum;
                }
            }

            const response = await this.makeRequest('/auth/login/', {
                method: 'POST',
                body: JSON.stringify(loginData),
            });

            if (response.success) {
                if (response.mfa_required) {
                    return {
                        success: true,
                        mfa_required: true,
                        mfa_token: response.mfa_token,
                        message: response.message || 'Please enter code from Microsoft Authenticator'
                    };
                }

                const accessToken =
                    this.normalizeToken(response?.data?.access_token) ||
                    this.normalizeToken(response?.data?.token) ||
                    this.normalizeToken(response?.access_token) ||
                    this.normalizeToken(response?.token);

                if (!accessToken) {
                    return {
                        success: false,
                        error: 'Login response did not include a valid access token',
                    };
                }

                this.setToken(accessToken);
                this.setRefreshToken(response?.data?.refresh_token || response?.refresh_token);
                return {
                    success: true,
                    token: accessToken,
                    user: response.data.user,
                    data: response.data, // Include full response data for site information
                    message: response.message
                };
            }

            // Extract error message from response
            let errorMsg = response.message || response.error || 'Login failed';
            if (response.errors && response.errors.non_field_errors) {
                errorMsg = Array.isArray(response.errors.non_field_errors)
                    ? response.errors.non_field_errors.join(', ')
                    : String(response.errors.non_field_errors);
            }

            return {
                success: false,
                error: errorMsg,
                errorCode: response.error,
                remaining_attempts: response.remaining_attempts,
                lockout_remaining_minutes: response.lockout_remaining_minutes
            };
        } catch (error) {
            // Extract better error message from error object
            let errorMsg = error.message || 'Login failed';
            if (error.response) {
                // Check if response has better error message
                if (error.response.errors && error.response.errors.non_field_errors) {
                    errorMsg = Array.isArray(error.response.errors.non_field_errors)
                        ? error.response.errors.non_field_errors.join(', ')
                        : String(error.response.errors.non_field_errors);
                } else if (error.response.message) {
                    errorMsg = error.response.message;
                } else if (error.response.error) {
                    errorMsg = error.response.error;
                }
            }

            return {
                success: false,
                error: errorMsg,
                errorCode: error.response?.error,
                remaining_attempts: error.response?.remaining_attempts,
                lockout_remaining_minutes: error.response?.lockout_remaining_minutes
            };
        }
    }

    // Get Active Companies
    async getActiveCompanies() {
        try {
            const response = await this.makeRequest('/auth/companies/active/');
            if (response.success) {
                return {
                    success: true,
                    companies: response.companies
                };
            }
            return { success: false, error: response.message };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getNavigationModules(siteCode = null) {
        const normalizedSite = this.normalizeText(siteCode) ||
            this.normalizeText(sessionStorage.getItem('tenant')) ||
            this.normalizeText(localStorage.getItem('currentSite')) ||
            'NO_SITE';
        const tokenSeed = this.normalizeToken(sessionStorage.getItem('access_token') || this.token) ||
            this.refreshTokenMemory ||
            'NO_TOKEN';
        const dedupKey = `getNavigationModules:${normalizedSite}:${tokenSeed}`;
        return _dedupRequestWithTtl(dedupKey, _NAV_MODULES_CACHE_TTL, () => this._getNavigationModulesImpl(siteCode));
    }

    async _getNavigationModulesImpl(siteCode = null) {
        const accessToken = this.normalizeToken(sessionStorage.getItem('access_token') || this.token);
        const token = accessToken || await this.refreshAccessToken();
        if (!token) {
            return {
                success: false,
                modules: [],
                error: 'Authentication required',
                unauthorized: true,
            };
        }

        try {
            const normalizedSite = this.normalizeText(siteCode) ||
                this.normalizeText(sessionStorage.getItem('tenant')) ||
                this.normalizeText(localStorage.getItem('currentSite'));

            const query = normalizedSite ? `?site_code=${encodeURIComponent(normalizedSite)}` : '';
            const response = await this.makeRequest(`/navigation/modules/${query}`);

            if (response.success) {
                return {
                    success: true,
                    modules: Array.isArray(response.modules) ? response.modules : [],
                    site_code: response.site_code,
                    company_id: response.company_id,
                };
            }

            return {
                success: false,
                modules: [],
                error: response.message || response.error || 'Failed to load navigation modules'
            };
        } catch (error) {
            return {
                success: false,
                modules: [],
                error: error.message || 'Failed to load navigation modules'
            };
        }
    }

    async upsertNavigationRolePolicy(payload) {
        try {
            const response = await this.makeRequest('/navigation/admin/role-policy/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to save role policy' };
        }
    }

    async getNavigationRolePolicies({ companyId = null, siteId = null, scopeLevel = null, roleId = null, roleName = null } = {}) {
        try {
            const params = new URLSearchParams();
            if (companyId) params.append('company_id', String(companyId));
            if (siteId) params.append('site_id', String(siteId));
            if (scopeLevel) params.append('scope_level', String(scopeLevel));
            if (roleId) params.append('role_id', String(roleId));
            if (roleName) params.append('role_name', String(roleName));

            const query = params.toString();
            const response = await this.makeRequest(`/navigation/admin/role-policies/${query ? `?${query}` : ''}`);
            return {
                success: !!response.success,
                policies: Array.isArray(response.policies) ? response.policies : [],
                allowed_module_keys: Array.isArray(response.allowed_module_keys) ? response.allowed_module_keys : [],
                count: Number(response.count || 0),
                error: response.error || response.message,
            };
        } catch (error) {
            return { success: false, policies: [], allowed_module_keys: [], count: 0, error: error.message || 'Failed to load role policies' };
        }
    }

    async upsertNavigationUserOverride(payload) {
        try {
            const response = await this.makeRequest('/navigation/admin/user-override/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to save user override' };
        }
    }

    async getNavigationUserOverrides({ userId = null, companyId = null, siteId = null, scopeLevel = null } = {}) {
        try {
            const params = new URLSearchParams();
            if (userId) params.append('user_id', String(userId));
            if (companyId) params.append('company_id', String(companyId));
            if (siteId) params.append('site_id', String(siteId));
            if (scopeLevel) params.append('scope_level', String(scopeLevel));

            const query = params.toString();
            const response = await this.makeRequest(`/navigation/admin/user-overrides/${query ? `?${query}` : ''}`);
            return {
                success: !!response.success,
                overrides: Array.isArray(response.overrides) ? response.overrides : [],
                allowed_module_keys: Array.isArray(response.allowed_module_keys) ? response.allowed_module_keys : [],
                denied_module_keys: Array.isArray(response.denied_module_keys) ? response.denied_module_keys : [],
                count: Number(response.count || 0),
                error: response.error || response.message,
            };
        } catch (error) {
            return { success: false, overrides: [], allowed_module_keys: [], denied_module_keys: [], count: 0, error: error.message || 'Failed to load user overrides' };
        }
    }

    async upsertNavigationSubscription(payload) {
        try {
            const response = await this.makeRequest('/navigation/admin/subscription/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to save module subscription' };
        }
    }

    async getNavigationSubscriptions(companyId = null) {
        try {
            const query = companyId ? `?company_id=${encodeURIComponent(String(companyId))}` : '';
            const response = await this.makeRequest(`/navigation/admin/subscriptions/${query}`);
            return {
                success: !!response.success,
                subscriptions: Array.isArray(response.subscriptions) ? response.subscriptions : [],
                error: response.error || response.message,
            };
        } catch (error) {
            return { success: false, subscriptions: [], error: error.message || 'Failed to load subscriptions' };
        }
    }

    // User Registration (sends OTP)
    async signup(userData) {
        try {
            // Ensure site_name is included if provided
            const signupData = { ...userData };

            // Remove null/undefined values to avoid serializer issues
            Object.keys(signupData).forEach(key => {
                if (signupData[key] === null || signupData[key] === undefined) {
                    delete signupData[key];
                }
            });

            if (signupData.siteName && !signupData.site_name) {
                signupData.site_name = signupData.siteName;
            }

            const response = await this.makeRequest('/auth/register/', {
                method: 'POST',
                body: JSON.stringify(signupData),
            });

            if (response.success) {
                return {
                    success: true,
                    message: response.message,
                    email: response.data.email,
                    requiresVerification: response.data.requires_verification,
                };
            }

            return { success: false, error: response.message || response.detail || 'Registration failed' };
        } catch (error) {
            // Handle specific error types
            if (error.message.includes('Validation failed') || error.message.includes('SERIALIZER_ERROR')) {
                // Return the actual error message from the server
                return {
                    success: false,
                    error: error.message || 'Please check your inputs: All required fields must be filled correctly.'
                };
            } else if (error.message.includes('already exists')) {
                return {
                    success: false,
                    error: 'An account with this email already exists. Please use a different email or try logging in instead.'
                };
            }
            return { success: false, error: error.message || 'Registration failed. Please try again.' };
        }
    }

    // Verify OTP
    async verifyOTP(email, otp) {
        try {
            const response = await this.makeRequest('/auth/verify-otp/', {
                method: 'POST',
                body: JSON.stringify({ email, otp }),
            });

            if (response.success) {
                const accessToken =
                    this.normalizeToken(response?.data?.access_token) ||
                    this.normalizeToken(response?.data?.token);

                if (!accessToken) {
                    return { success: false, error: 'Verification response did not include a valid access token' };
                }

                this.setToken(accessToken);
                this.setRefreshToken(response?.data?.refresh_token || response?.refresh_token || null);
                return {
                    success: true,
                    message: response.message,
                    user: response.data.user,
                    token: accessToken,
                };
            }

            return { success: false, error: response.message, errorCode: response.error };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Resend OTP
    async resendOTP(email) {
        try {
            const response = await this.makeRequest('/auth/resend-otp/', {
                method: 'POST',
                body: JSON.stringify({ email }),
            });

            if (response.success) {
                return {
                    success: true,
                    message: response.message,
                };
            }

            return { success: false, error: response.message };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Check username availability (optionally for a specific site)
    async checkUsernameAvailability(username, siteId = null, siteName = null, siteCode = null) {
        try {
            let url = `/auth/check-username/?username=${encodeURIComponent(username)}`;
            if (siteId) {
                url += `&site_id=${encodeURIComponent(siteId)}`;
            } else if (siteName) {
                url += `&site_name=${encodeURIComponent(siteName)}`;
            } else if (siteCode) {
                url += `&site_code=${encodeURIComponent(siteCode)}`;
            }

            const response = await this.makeRequest(url);
            return {
                success: true,
                available: response.available || false,
                message: response.message || ''
            };
        } catch (error) {
            return {
                success: false,
                available: false,
                error: error.message
            };
        }
    }

    // Get current user profile
    async getCurrentUser() {
        const tokenSeed = this.normalizeToken(sessionStorage.getItem('access_token') || this.token) ||
            this.refreshTokenMemory ||
            'NO_TOKEN';
        return _dedupRequestWithTtl(`getCurrentUser:${tokenSeed}`, _AUTH_ME_CACHE_TTL, () => this._getCurrentUserImpl());
    }

    async _getCurrentUserImpl() {
        try {
            // Ensure we have the latest token before making the request
            const accessToken = this.normalizeToken(sessionStorage.getItem('access_token'));
            const token = accessToken || await this.refreshAccessToken();
            if (token && token !== this.token) {
                this.token = token;
            }

            // Don't attempt request if no token exists
            if (!token) {
                return {
                    success: false,
                    error: 'Authentication credentials were not provided.'
                };
            }

            const response = await this.makeRequest('/auth/me/');

            // Handle different response structures
            if (response.data && response.data.user) {
                return {
                    success: true,
                    user: response.data.user,
                };
            } else if (response.user) {
                // Handle case where response is directly the user object
                return {
                    success: true,
                    user: response.user,
                };
            } else {
                return { success: false, error: 'Invalid response format' };
            }
        } catch (error) {
            // Handle 401 errors gracefully - token might be expired or invalid
            if (error.message && (error.message.includes('401') || error.message.includes('Unauthorized') || error.message.includes('credentials'))) {
                // Clear invalid token
                this.clearAuthState();
                return {
                    success: false,
                    error: 'Authentication credentials were not provided.'
                };
            }
            return { success: false, error: error.message };
        }
    }

    // Update user profile
    async updateProfile(profileData) {
        try {
            const response = await this.makeRequest('/auth/profile/', {
                method: 'PUT',
                body: JSON.stringify(profileData),
            });

            return {
                success: true,
                message: response.message,
                user: response.data.user,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Change user password
    async changePassword(passwordData) {
        try {
            const response = await this.makeRequest('/auth/change-password/', {
                method: 'POST',
                body: JSON.stringify(passwordData),
            });

            return {
                success: true,
                message: response.message,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Get dashboard data
    async getDashboardData() {
        try {
            const response = await this.makeRequest('/dashboard/');
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Get dashboard statistics
    async getDashboardStats() {
        try {
            const response = await this.makeRequest('/dashboard/stats/');
            return {
                success: true,
                data: response.data,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Get all users (admin only)
    async getUsers(companyId = null, siteId = null, siteCode = null, { skipStorageFallback = false } = {}) {
        try {
            const params = new URLSearchParams();
            const resolvedCompanyId = skipStorageFallback
                ? this.normalizeId(companyId)
                : (this.normalizeId(companyId) ||
                    this.normalizeId(sessionStorage.getItem('company_id')) ||
                    this.normalizeId(localStorage.getItem('company_id')));
            const resolvedSiteId = skipStorageFallback
                ? this.normalizeId(siteId)
                : (this.normalizeId(siteId) ||
                    this.normalizeId(sessionStorage.getItem('site_id')) ||
                    this.normalizeId(localStorage.getItem('site_id')));
            const resolvedSiteCode = skipStorageFallback
                ? this.normalizeText(siteCode)
                : (this.normalizeText(siteCode) ||
                    this.normalizeText(sessionStorage.getItem('tenant')) ||
                    this.normalizeText(localStorage.getItem('currentSite')));
            const resolvedUserId = this.normalizeId(sessionStorage.getItem('user_id'));

            if (resolvedCompanyId) params.append('company_id', String(resolvedCompanyId));
            if (resolvedSiteId) params.append('site_id', String(resolvedSiteId));
            if (resolvedSiteCode) params.append('site_code', String(resolvedSiteCode));
            if (resolvedUserId) params.append('user_id', String(resolvedUserId));
            const query = params.toString();
            const endpoint = `/admin/users/manage/${query ? `?${query}` : ''}`;
            const response = await this.makeRequest(endpoint);
            const users = response.data?.users || response.results || response.data || [];
            if (Array.isArray(users)) {
                return {
                    success: true,
                    users,
                    count: response.data?.count || users.length || 0
                };
            }
            return { success: false, error: 'Unexpected users response', users: [] };
        } catch (error) {
            return { success: false, error: error.message, users: [] };
        }
    }

    // Get single user (admin only)
    async getUser(userId) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/`);
            return {
                success: true,
                user: response.data.user,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // System Admin Data Cleanup
    async getCleanupUsers(params = {}) {
        try {
            const query = new URLSearchParams();
            if (params.search) query.append('search', params.search);
            if (params.company_id) query.append('company_id', String(params.company_id));
            if (params.site_id) query.append('site_id', String(params.site_id));
            const endpoint = `/admin/data-cleanup/users/${query.toString() ? `?${query.toString()}` : ''}`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                users: response?.data?.users || [],
                count: response?.data?.count || 0,
            };
        } catch (error) {
            return { success: false, error: error.message, users: [], count: 0 };
        }
    }

    async getCleanupSummary(params = {}) {
        try {
            const query = new URLSearchParams();
            if (params.user_id) query.append('user_id', String(params.user_id));
            if (params.company_id) query.append('company_id', String(params.company_id));
            if (params.site_id) query.append('site_id', String(params.site_id));
            if (Array.isArray(params.module_keys)) {
                params.module_keys.forEach((key) => {
                    if (key) query.append('module_key', key);
                });
            }
            const endpoint = `/admin/data-cleanup/summary/${query.toString() ? `?${query.toString()}` : ''}`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                summary: response?.data || null,
            };
        } catch (error) {
            return { success: false, error: error.message, summary: null };
        }
    }

    async executeCleanup(payload) {
        try {
            const response = await this.makeRequest('/admin/data-cleanup/execute/', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            return {
                success: true,
                message: response?.message || 'Cleanup completed.',
                result: response?.data || null,
            };
        } catch (error) {
            return { success: false, error: error.message, result: null };
        }
    }

    async deleteCleanupBackup(backupFilePath) {
        try {
            const query = new URLSearchParams({ backup_file_path: backupFilePath });
            const response = await this.makeRequest(`/admin/data-cleanup/execute/?${query.toString()}`, {
                method: 'DELETE',
            });
            return {
                success: true,
                message: response?.message || 'Backup deleted successfully.',
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getCleanupBackupHistory() {
        try {
            const response = await this.makeRequest('/admin/data-cleanup/history/');
            return {
                success: true,
                data: response?.data || [],
            };
        } catch (error) {
            return { success: false, error: error.message, data: [] };
        }
    }

    // Update user (admin only)
    async updateUser(userId, userData) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/`, {
                method: 'PUT',
                body: JSON.stringify(userData),
            });

            return {
                success: true,
                message: response.message,
                user: response.data?.user || response.user,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Delete user (admin only)
    async deleteUser(userId) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/`, {
                method: 'DELETE',
            });

            return {
                success: true,
                message: response.message,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Health check
    async healthCheck() {
        try {
            const baseRoot = this.baseURL.endsWith('/api')
                ? this.baseURL.slice(0, -4)
                : this.baseURL;
            const response = await fetch(`${baseRoot}/health/`);
            const data = await response.json();
            return {
                success: true,
                data,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Password Reset - Request reset token
    async forgotPassword(email, siteId = null, siteName = null) {
        try {
            const requestBody = { email };

            // Add site information if provided
            if (siteId) {
                requestBody.site_id = siteId;
            }
            if (siteName) {
                requestBody.site_name = siteName;
            }

            const response = await this.makeRequest('/auth/forgot-password/', {
                method: 'POST',
                body: JSON.stringify(requestBody),
            });

            // Check if response indicates success
            if (response.success) {
                return {
                    success: true,
                    message: response.message,
                    resetToken: response.resetToken, // Only in development
                    resetUrl: response.resetUrl, // Only in development
                    site_id: response.site_id
                };
            } else {
                // Handle validation errors or other failures
                return {
                    success: false,
                    error: response.message || response.error || 'Failed to send reset link',
                    errorCode: response.error
                };
            }
        } catch (error) {
            // Extract error message from error response
            let errorMsg = error.message || 'Failed to send reset link';
            if (error.response) {
                if (error.response.errors) {
                    const errorMessages = [];
                    for (const [field, messages] of Object.entries(error.response.errors)) {
                        if (Array.isArray(messages)) {
                            errorMessages.push(...messages);
                        } else {
                            errorMessages.push(messages);
                        }
                    }
                    errorMsg = errorMessages.join(', ') || error.response.message || errorMsg;
                } else if (error.response.message) {
                    errorMsg = error.response.message;
                }
            }
            return { success: false, error: errorMsg };
        }
    }

    // Password Reset - Reset with token
    async resetPassword(token, password, passwordConfirm, siteId = null) {
        try {
            const requestBody = {
                token,
                password,
                password_confirm: passwordConfirm || password // Use password_confirm if provided, else use password
            };

            // Add site_id if provided (required for site verification)
            if (siteId) {
                requestBody.site_id = siteId;
            }

            const response = await this.makeRequest('/auth/reset-password/', {
                method: 'POST',
                body: JSON.stringify(requestBody),
            });

            if (response.success) {
                const accessToken =
                    this.normalizeToken(response?.data?.access_token) ||
                    this.normalizeToken(response?.data?.token);

                if (!accessToken) {
                    return { success: false, error: 'Password reset response did not include a valid access token' };
                }

                this.setToken(accessToken);
                this.setRefreshToken(response?.data?.refresh_token || response?.refresh_token);
                return {
                    success: true,
                    message: response.message,
                    user: response.data.user,
                    token: accessToken,
                };
            }

            // Handle validation errors
            if (response.errors) {
                const errorMessages = [];
                for (const [field, messages] of Object.entries(response.errors)) {
                    if (Array.isArray(messages)) {
                        errorMessages.push(...messages);
                    } else {
                        errorMessages.push(messages);
                    }
                }
                return { success: false, error: errorMessages.join(', ') || response.message };
            }

            return { success: false, error: response.message || 'Password reset failed' };
        } catch (error) {
            // Extract error message from error response
            let errorMsg = error.message || 'Password reset failed';
            if (error.response) {
                if (error.response.errors) {
                    const errorMessages = [];
                    for (const [field, messages] of Object.entries(error.response.errors)) {
                        if (Array.isArray(messages)) {
                            errorMessages.push(...messages);
                        } else {
                            errorMessages.push(messages);
                        }
                    }
                    errorMsg = errorMessages.join(', ') || error.response.message || errorMsg;
                } else if (error.response.message) {
                    errorMsg = error.response.message;
                }
            }
            return { success: false, error: errorMsg };
        }
    }

    // Logout
    async logout() {
        const hasAccessToken = !!this.normalizeToken(sessionStorage.getItem('access_token') || this.token);
        const hasRefreshToken = !!this.refreshTokenMemory;
        if (!hasAccessToken && !hasRefreshToken) {
            this.clearAuthState();
            return;
        }
        try {
            await this.makeRequest('/auth/logout/', {
                method: 'POST',
                body: JSON.stringify({ refresh: this.refreshTokenMemory || undefined }),
            });
        } catch (error) {
            // Ignore logout API failures and always clear local auth state.
        }
        this.clearAuthState();
    }

    // Admin User Authentication
    async adminLogin(username, password) {
        try {
            const response = await this.makeRequest('/admin/auth/login/', {
                method: 'POST',
                body: JSON.stringify({ username, password }),
            });

            if (response.success) {
                const accessToken =
                    this.normalizeToken(response?.data?.access_token) ||
                    this.normalizeToken(response?.data?.token);

                if (!accessToken) {
                    return { success: false, error: 'Admin login response did not include a valid access token' };
                }

                this.setToken(accessToken);
                this.setRefreshToken(response?.data?.refresh_token || response?.refresh_token);
                return {
                    success: true,
                    token: accessToken,
                    admin_user: response.data.admin_user,
                };
            }

            return { success: false, error: response.message || 'Admin login failed' };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Get all admin users (admin only)
    async getAdminUsers() {
        try {
            const response = await this.makeRequest('/admin/users/');
            return {
                success: true,
                admin_users: response.data?.admin_users || [],
                count: response.data?.count || 0
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Create admin user (superadmin only)
    async createAdminUser(adminData) {
        try {
            const response = await this.makeRequest('/admin/users/create/', {
                method: 'POST',
                body: JSON.stringify(adminData),
            });

            return {
                success: true,
                message: response.message,
                admin_user: response.data?.admin_user,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Get admin activity logs
    async getAdminActivityLogs() {
        try {
            const response = await this.makeRequest('/admin/activity-logs/');
            return {
                success: true,
                logs: response.data?.logs || [],
                count: response.data?.count || 0
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    /**
     * Get all sites
     * @param {boolean} activeOnly
     * @param {string} search
     * @param {string|number|null} companyId
     */
    async getSites(activeOnly = true, search = '', companyId = null) {
        const dedupKey = `getSites:${activeOnly}:${search}:${companyId}`;
        return _dedupRequestWithTtl(dedupKey, _SITES_CACHE_TTL, () => this._getSitesImpl(activeOnly, search, companyId));
    }

    async _getSitesImpl(activeOnly = true, search = '', companyId = null) {
        try {
            const params = new URLSearchParams();
            // Always explicitly set active_only parameter
            params.append('active_only', activeOnly ? 'true' : 'false');
            if (search) params.append('search', search);
            if (companyId) params.append('company_id', companyId);

            const queryString = params.toString();
            const isAdminRoute = typeof window !== 'undefined' && window.location?.pathname?.startsWith('/admin');
            const endpointBase = isAdminRoute ? '/admin/sites/' : '/auth/sites/';
            const endpoint = `${endpointBase}${queryString ? `?${queryString}` : ''}`;

            const response = await this.makeRequest(endpoint);

            // Handle server-side failure responses explicitly
            if (response && response.success === false) {
                return {
                    success: false,
                    error: response.message || response.error || 'Failed to fetch sites',
                    sites: []
                };
            }

            // Handle different response structures
            let sites = [];
            let count = 0;

            if (response.data && response.data.sites) {
                // Standard structure: { success: true, data: { sites: [...], count: N } }
                sites = response.data.sites;
                count = response.data.count || sites.length;
            } else if (response.sites) {
                // Alternative structure: { success: true, sites: [...] }
                sites = Array.isArray(response.sites) ? response.sites : [];
                count = response.count || sites.length;
            } else if (Array.isArray(response)) {
                // Direct array response
                sites = response;
                count = sites.length;
            }

            return {
                success: true,
                sites: sites,
                count: count
            };
        } catch (error) {
            console.error('getSites error:', error);
            return { success: false, error: error.message, sites: [] };
        }
    }

    /**
     * Get sites by company code
     * @param {string} companyCode
     * @param {boolean} activeOnly
     */
    async getSitesByCompanyCode(companyCode, activeOnly = true) {
        if (!companyCode) {
            return { success: false, error: 'Company code is required', sites: [] };
        }

        try {
            const params = new URLSearchParams();
            params.append('active_only', activeOnly ? 'true' : 'false');
            params.append('company_code', companyCode);

            const queryString = params.toString();
            const endpoint = `/auth/sites/?${queryString}`;

            const response = await this.makeRequest(endpoint);

            let sites = [];
            let count = 0;

            if (response.data && response.data.sites) {
                sites = response.data.sites;
                count = response.data.count || sites.length;
            } else if (response.sites) {
                sites = Array.isArray(response.sites) ? response.sites : [];
                count = response.count || sites.length;
            } else if (Array.isArray(response)) {
                sites = response;
                count = sites.length;
            }

            return {
                success: true,
                sites: sites,
                count: count
            };
        } catch (error) {
            console.error('getSitesByCompanyCode error:', error);
            return { success: false, error: error.message, sites: [] };
        }
    }

    /**
     * Get available roles for user creation
     */
    async getRoles() {
        const token = sessionStorage.getItem('access_token') || this.token;
        if (!token) {
            return {
                success: false,
                roles: [],
                error: 'Authentication required',
                unauthorized: true,
            };
        }

        try {
            const response = await this.makeRequest('/admin/roles/');
            return {
                success: true,
                roles: response.data || [],
            };
        } catch (error) {
            console.error('getRoles error:', error);
            // Fallback for development if endpoint fails
            return {
                success: true,
                roles: [
                    { role_id: 1, role_name: 'system_admin', display_name: 'System Admin', level: 1 },
                    { role_id: 2, role_name: 'ho_admin', display_name: 'HO Admin', level: 2 },
                    { role_id: 3, role_name: 'site_admin', display_name: 'Site Admin', level: 3 },
                    { role_id: 4, role_name: 'general_user', display_name: 'General User', level: 3 }
                ]
            };
        }
    }

    async getAdminOnboardingSchema() {
        const token = sessionStorage.getItem('access_token') || this.token;
        if (!token) {
            return {
                success: false,
                error: 'Authentication required',
                unauthorized: true,
            };
        }

        try {
            const response = await this.makeRequest('/admin/onboarding/schema/');
            return {
                success: response?.success !== false,
                schema: response?.entities || null,
                uploadNotes: Array.isArray(response?.upload_notes) ? response.upload_notes : [],
                raw: response,
            };
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Failed to load onboarding schema',
            };
        }
    }

    // Get dropdown values from database (for form selects)
    async getDropdowns(dropdownType = null) {
        try {
            const endpoint = dropdownType 
                ? `/admin/dropdowns/${dropdownType}/`
                : '/admin/dropdowns/';
            
            const response = await this.makeRequest(endpoint);
            
            if (dropdownType) {
                // Single dropdown type response
                return {
                    success: response?.success !== false,
                    dropdownType: response?.dropdown_type,
                    values: Array.isArray(response?.values) ? response.values : [],
                    raw: response,
                };
            } else {
                // All dropdowns response
                return {
                    success: response?.success !== false,
                    dropdowns: response?.dropdowns || {},
                    raw: response,
                };
            }
        } catch (error) {
return {
                success: false,
                error: error.message || 'Failed to load dropdowns',
                dropdowns: {},
                values: [],
            };
        }
    }

    // Get single site (works for both admin and regular endpoints)
    async getSite(siteId, useAdminEndpoint = false) {
        try {
            const endpoint = useAdminEndpoint
                ? `/admin/sites/${siteId}/`
                : `/auth/sites/${siteId}/`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                site: response.data?.site || response.site
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Update user's site
    async updateUserSite(siteId = null, siteCode = null, siteName = null) {
        try {
            const updateData = {};
            if (siteId) updateData.site_id = siteId;
            if (siteCode) updateData.site_code = siteCode;
            if (siteName) updateData.site_name = siteName;

            const response = await this.makeRequest('/auth/update-site/', {
                method: 'PUT',
                body: JSON.stringify(updateData),
            });

            return {
                success: true,
                message: response.message,
                user: response.data?.user,
                site: response.data?.site
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // ==================== ADMIN MANAGEMENT API ====================

    // Get comprehensive dashboard statistics (real-time)
    async getAdminDashboardStats(companyId = null, siteId = null, siteCode = null) {
        try {
            const params = new URLSearchParams();
            if (companyId) params.append('company_id', companyId);
            if (siteId) params.append('site_id', siteId);
            if (siteCode) params.append('site_code', siteCode);

            const query = params.toString();
            const response = await this.makeRequest(`/admin/dashboard/stats/${query ? `?${query}` : ''}`);
            const payload = response?.data || {};
            return {
                success: response?.success !== false,
                data: payload,
                ...payload
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // User Management
    async createUser(userData) {
        try {
            const response = await this.makeRequest('/admin/users/manage/', {
                method: 'POST',
                body: JSON.stringify(userData),
            });
            return {
                success: true,
                message: response.message,
                user: response.data?.user
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getUserStats() {
        try {
            const response = await this.makeRequest('/admin/users/manage/');
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Company Management Methods (Super Admin)
    async getCompanies() {
        const token = sessionStorage.getItem('access_token') || this.token;
        if (!token) {
            return {
                success: false,
                companies: [],
                count: 0,
                error: 'Authentication required',
                unauthorized: true,
            };
        }

        const normalizeCompanies = (rawList = []) =>
            (Array.isArray(rawList) ? rawList : []).map((c) => ({
                ...c,
                company_id: c.company_id ?? c.id ?? c.customer_id,
                company_name: c.company_name || '',
                company_code: (c.company_code || '').toUpperCase(),
                industry_type: c.industry_type || null,
                country: c.country || null,
                location: c.location || null,
                timezone: c.timezone || null,
                currency: c.currency || null,
                description: c.description || null,
                primary_contact_name: c.primary_contact_name ?? c.company_spoc_name ?? '',
                primary_contact_email: c.primary_contact_email ?? c.company_spoc_email ?? '',
                primary_contact_phone: c.primary_contact_phone ?? c.company_spoc_phone_no ?? '',
                primary_contact_phones: Array.isArray(c.primary_contact_phones)
                    ? c.primary_contact_phones
                    : [],
                portfolio_size: c.portfolio_size != null ? Number(c.portfolio_size) : null,
                plant_count: c.plant_count != null ? Number(c.plant_count) : null,
                logo_url: c.logo_url || null,
                subscription_plan: c.subscription_plan || 'basic',
                max_sites: Number(c.max_sites ?? 0) || 0,
                max_users: Number(c.max_users ?? 0) || 0,
                storage_limit_gb: Number(c.storage_limit_gb ?? 0) || 0,
                is_active: c.is_active !== undefined ? !!c.is_active : (c.active_status !== undefined ? !!c.active_status : true),
                is_trial: c.is_trial !== undefined ? !!c.is_trial : false,
                created_at: c.created_at || null,
                updated_at: c.updated_at || null,
            }));

        const dedupe = (items = []) => {
            const map = new Map();
            items.forEach((item) => {
                // Key by business identity (code or name) rather than internal DB IDs 
                // because records come from two different tables with different primary keys
                const key = String(item.company_code || item.company_name || item.company_id || '').trim().toUpperCase();
                if (!key) return;

                // If we already have this company, keep the one with more information (e.g. from super-admin table)
                if (map.has(key)) {
                    const existing = map.get(key);
                    // Prefer the one that has industry_type or was loaded first from super-admin
                    if (!existing.industry_type && item.industry_type) {
                        map.set(key, item);
                    }
                } else {
                    map.set(key, item);
                }
            });
            return Array.from(map.values());
        };

        const collected = [];
        let lastError = null;

        // Try new super-admin endpoint
        try {
            const saResponse = await this.makeRequest('/super-admin/companies/');
            const saCompanies = saResponse.companies || saResponse.data?.companies || saResponse.data || [];
            if (Array.isArray(saCompanies) && saCompanies.length > 0) {
                collected.push(...normalizeCompanies(saCompanies));
            }
        } catch (error) {
            // Log for debugging but continue to fallback
            if (error.status !== 403 && error.status !== 401 && process.env.NODE_ENV === 'development') {
}
            lastError = error;
        }

        // Legacy customer endpoint fallback removed to prevent duplicate keys and ghost data
        // We now strictly use the companies table as the source of truth

        const companies = dedupe(collected);
        if (companies.length > 0) {
            return {
                success: true,
                companies,
                count: companies.length
            };
        }

        if (lastError) {
            return { success: false, error: lastError.message, companies: [] };
        }

        return { success: true, companies: [], count: 0 };
    }

    async getCompany(companyId) {
        if (!companyId) {
            return { success: false, error: 'Company ID is required' };
        }

        try {
            const response = await this.makeRequest(`/super-admin/companies/${companyId}/`);
            const rawCompany = response.company || response.data?.company || response.data || null;

            if (!rawCompany) {
                return { success: false, error: 'Company not found' };
            }

            const company = {
                ...rawCompany,
                company_id: rawCompany.company_id ?? rawCompany.id ?? rawCompany.customer_id,
                company_name: rawCompany.company_name || '',
                company_code: (rawCompany.company_code || '').toUpperCase(),
                industry_type: rawCompany.industry_type || null,
                country: rawCompany.country || null,
                location: rawCompany.location || null,
                timezone: rawCompany.timezone || null,
                currency: rawCompany.currency || null,
                primary_contact_name: rawCompany.primary_contact_name ?? rawCompany.company_spoc_name ?? '',
                primary_contact_email: rawCompany.primary_contact_email ?? rawCompany.company_spoc_email ?? '',
                primary_contact_phone: rawCompany.primary_contact_phone ?? rawCompany.company_spoc_phone_no ?? '',
                primary_contact_phones: rawCompany.primary_contact_phones ?? [],
                subscription_plan: rawCompany.subscription_plan || 'basic',
                max_sites: Number(rawCompany.max_sites ?? 0) || 0,
                max_users: Number(rawCompany.max_users ?? 0) || 0,
                storage_limit_gb: Number(rawCompany.storage_limit_gb ?? 0) || 0,
                is_active: rawCompany.is_active !== undefined ? !!rawCompany.is_active : true,
                is_trial: rawCompany.is_trial !== undefined ? !!rawCompany.is_trial : false,
            };

            return { success: true, company };
        } catch (error) {
            return { success: false, error: error.message || 'Failed to load company' };
        }
    }

    async createCompany(companyData) {
        try {
            const response = await this.makeRequest('/super-admin/companies/', {
                method: 'POST',
                body: JSON.stringify(companyData),
            });
            return {
                success: true,
                message: response.message,
                company: response.company || response.customer
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async updateCompany(companyId, companyData) {
        try {
            const response = await this.makeRequest(`/super-admin/companies/${companyId}/`, {
                method: 'PUT',
                body: JSON.stringify(companyData),
            });
            return {
                success: true,
                message: response.message,
                company: response.company || response.customer
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteCompany(companyId) {
        try {
            const response = await this.makeRequest(`/super-admin/companies/${companyId}/`, {
                method: 'DELETE',
            });
            return {
                success: true,
                message: response.message || 'Company deleted successfully'
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async provisionCompany(companyId) {
        try {
            const response = await this.makeRequest(`/super-admin/companies/${companyId}/provision/`, {
                method: 'POST',
            });
            return {
                success: true,
                message: response.message,
                database_name: response.database_name
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Customer Management Methods (Legacy - to be replaced by Company)
    async getCustomers() {
        try {
            const response = await this.makeRequest('/admin/customers/');
            return {
                success: true,
                customers: response.customers || [],
                count: response.count || 0
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async createCustomer(customerData) {
        try {
            const response = await this.makeRequest('/admin/customers/', {
                method: 'POST',
                body: JSON.stringify(customerData),
            });
            return {
                success: true,
                message: response.message,
                customer: response.customer
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async updateCustomer(customerId, customerData) {
        try {
            const response = await this.makeRequest(`/admin/customers/${customerId}/`, {
                method: 'PUT',
                body: JSON.stringify(customerData),
            });
            return {
                success: true,
                message: response.message,
                customer: response.customer
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteCustomer(customerId) {
        try {
            const response = await this.makeRequest(`/admin/customers/${customerId}/`, {
                method: 'DELETE',
            });
            return {
                success: true,
                message: response.message || 'Customer deleted successfully'
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async activateUser(userId, isActive = true) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/activate/`, {
                method: 'PUT',
                body: JSON.stringify({ is_active: isActive }),
            });
            return {
                success: true,
                message: response.message,
                user: response.data?.user
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async resetUserPassword(userId, newPassword) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/reset-password/`, {
                method: 'POST',
                body: JSON.stringify({ new_password: newPassword }),
            });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async unlockUser(userId) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/unlock/`, {
                method: 'POST',
            });
            return {
                success: true,
                message: response.message,
                user: response.data?.user
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getUserLoginHistory(userId, limit = 50) {
        try {
            const response = await this.makeRequest(`/admin/users/${userId}/login-history/?limit=${limit}`);
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Site Management
    async createSite(siteData) {
        try {
            const response = await this.makeRequest('/auth/sites/create/', {
                method: 'POST',
                body: JSON.stringify(siteData),
            });
            return {
                success: true,
                message: response.message,
                site: response.data?.site
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getSiteStats() {
        try {
            const response = await this.makeRequest('/auth/sites/');
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async updateSite(siteId, siteData) {
        try {
            const response = await this.makeRequest(`/auth/sites/${siteId}/update/`, {
                method: 'PUT',
                body: JSON.stringify(siteData),
            });
            return {
                success: true,
                message: response.message,
                site: response.data?.site
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async deleteSite(siteId) {
        try {
            const response = await this.makeRequest(`/auth/sites/${siteId}/delete/`, {
                method: 'DELETE',
            });
            return {
                success: true,
                message: response.message
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async activateSite(siteId, isActive = true) {
        try {
            const response = await this.makeRequest(`/auth/sites/${siteId}/update/`, {
                method: 'PUT',
                body: JSON.stringify({ is_active: isActive }),
            });
            return {
                success: true,
                message: response.message,
                site: response.data?.site
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getSiteUsers(siteId) {
        try {
            const response = await this.makeRequest(`/admin/sites/${siteId}/users/`);
            const payload = response?.data || response || {};
            return {
                success: true,
                users: Array.isArray(payload.users) ? payload.users : [],
                user_count: typeof payload.user_count === 'number'
                    ? payload.user_count
                    : (Array.isArray(payload.users) ? payload.users.length : 0),
                site: payload.site || null
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // Security & Monitoring
    async getLoginAttempts(limit = 100, failedOnly = false) {
        try {
            const params = new URLSearchParams();
            params.append('limit', limit);
            params.append('failed_only', failedOnly);
            const response = await this.makeRequest(`/admin/security/login-attempts/?${params.toString()}`);
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getLockedAccounts() {
        try {
            const response = await this.makeRequest('/admin/security/locked-accounts/');
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getSuspiciousIPs(hours = 24, threshold = 5) {
        try {
            const params = new URLSearchParams();
            params.append('hours', hours);
            params.append('threshold', threshold);
            const response = await this.makeRequest(`/admin/security/suspicious-ips/?${params.toString()}`);
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getSecurityStats() {
        try {
            const response = await this.makeRequest('/admin/security/stats/');
            return {
                success: true,
                ...response.data
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // ==================== USER ACTIVITY LOG METHODS ====================

    /**
     * Log user activity
     * @param {Object} activityData - Activity data to log
     * @returns {Promise<Object>}
     */
    async logUserActivity(activityData) {
        try {
            const activeSiteContext = this.getActiveSiteContext();
            const payload = {
                ...activityData,
                metadata: {
                    ...(activityData?.metadata || {}),
                    ...(activeSiteContext.siteCode ? { site_code: activeSiteContext.siteCode } : {}),
                    ...(activeSiteContext.siteId ? { site_id: activeSiteContext.siteId } : {}),
                },
            };
            const endpoint = '/auth/activity-log/';
            const response = await this.makeRequest(endpoint, {
                method: 'POST',
                body: JSON.stringify(payload)
            });
            return response;
        } catch (error) {
            console.error('Error logging user activity:', error);
            throw error;
        }
    }

    /**
     * Get user activity logs
     * @param {Object} params - Query parameters (page, page_size, activity_type, start_date, end_date)
     * @returns {Promise<Object>}
     */
    async getUserActivityLogs(params = {}) {
        try {
            const queryParams = new URLSearchParams();
            const activeSiteContext = this.getActiveSiteContext();
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            if (params.activity_type) queryParams.append('activity_type', params.activity_type);
            if (params.start_date) queryParams.append('start_date', params.start_date);
            if (params.end_date) queryParams.append('end_date', params.end_date);
            if (activeSiteContext.siteCode) queryParams.append('site_code', activeSiteContext.siteCode);

            const endpoint = `/auth/activity-log/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await this.makeRequest(endpoint);
            return response;
        } catch (error) {
            console.error('Error fetching user activity logs:', error);
            throw error;
        }
    }

    /**
     * Get user activity statistics
     * @param {Object} params - Query parameters (days)
     * @returns {Promise<Object>}
     */
    async getUserActivityStats(params = {}) {
        try {
            const queryParams = new URLSearchParams();
            const activeSiteContext = this.getActiveSiteContext();
            if (params.days) queryParams.append('days', params.days);
            if (activeSiteContext.siteCode) queryParams.append('site_code', activeSiteContext.siteCode);

            const endpoint = `/auth/activity-stats/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
            const response = await this.makeRequest(endpoint);
            return response;
        } catch (error) {
            console.error('Error fetching user activity stats:', error);
            throw error;
        }
    }

    /**
     * Get all users' activity logs (Admin only)
     * @param {Object} params - Query parameters (user_id, username, activity_type, page, page_size, start_date, end_date)
     * @returns {Promise<Object>}
     */
    async getAdminUserActivityLogs(params = {}) {
        try {
            const queryParams = new URLSearchParams();
            if (params.user_id) queryParams.append('user_id', params.user_id);
            if (params.username) queryParams.append('username', params.username);
            if (params.activity_type) queryParams.append('activity_type', params.activity_type);
            if (params.page) queryParams.append('page', params.page);
            if (params.page_size) queryParams.append('page_size', params.page_size);
            if (params.start_date) queryParams.append('start_date', params.start_date);
            if (params.end_date) queryParams.append('end_date', params.end_date);

            const queryString = queryParams.toString();
            const endpoint = `/admin/user-activity/${queryString ? '?' + queryString : ''}`;
            const response = await this.makeRequest(endpoint);
            return response;
        } catch (error) {
            console.error('Error fetching admin user activity logs:', error);
            throw error;
        }
    }

    // ==================== O&M MODULE METHODS ====================

    /**
     * Get PM Calendar Events
     * @param {string} siteCode
     */
    /**
     * Get PM Calendar Events
     * @param {string} siteCode
     * @param {string} [startDate] - optional ISO date YYYY-MM-DD
     * @param {string} [endDate]   - optional ISO date YYYY-MM-DD
     */
    async getPMCalendarEvents(siteCode, startDate, endDate) {
        try {
            const params = new URLSearchParams();
            if (siteCode) params.set('site_code', siteCode);
            if (startDate) params.set('start', startDate);
            if (endDate) params.set('end', endDate);
            const qs = params.toString();
            const endpoint = `/om/pm-events/${qs ? `?${qs}` : ''}`;
            const response = await this.makeRequest(endpoint);
            // The viewset returns standard DRF paginated response or list
            const events = response.results || response.data || response || [];
            return {
                success: true,
                events: Array.isArray(events) ? events : []
            };
        } catch (error) {
            console.error('getPMCalendarEvents error:', error);
            return { success: false, error: error.message, events: [] };
        }
    }

    /**
     * Get site-scoped users assignable in PM Calendar task forms.
     * @param {string} siteCode
     */
    async getPMAssignableUsers(siteCode) {
        try {
            const params = new URLSearchParams();
            if (siteCode) params.set('site_code', siteCode);
            const qs = params.toString();
            const endpoint = `/om/pm-events/assignable-users/${qs ? `?${qs}` : ''}`;
            const response = await this.makeRequest(endpoint);
            const users = response?.users || response?.data?.users || [];
            return {
                success: true,
                users: Array.isArray(users) ? users : [],
                count: typeof response?.count === 'number' ? response.count : (Array.isArray(users) ? users.length : 0),
                site: response?.site || null,
            };
        } catch (error) {
            console.error('getPMAssignableUsers error:', error);
            return { success: false, error: error.message, users: [], count: 0, site: null };
        }
    }

    /**
     * Save PM Calendar Event (Create or Update)
     * @param {Object} eventData
     */
    async savePMCalendarEvent(eventData) {
        try {
            const isUpdate = !!eventData.id && !isNaN(eventData.id);
            const endpoint = isUpdate ? `/om/pm-events/${eventData.id}/` : '/om/pm-events/';
            const method = isUpdate ? 'PUT' : 'POST';

            const response = await this.makeRequest(endpoint, {
                method: method,
                body: JSON.stringify(eventData),
            });

            return {
                success: true,
                event: response
            };
        } catch (error) {
            console.error('savePMCalendarEvent error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Delete PM Calendar Event
     * @param {string|number} eventId
     */
    async deletePMCalendarEvent(eventId) {
        try {
            await this.makeRequest(`/om/pm-events/${eventId}/`, {
                method: 'DELETE',
            });
            return { success: true };
        } catch (error) {
            console.error('deletePMCalendarEvent error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Update PM Calendar Event Status
     * @param {string|number} eventId
     * @param {string} status
     */
    async updatePMCalendarStatus(eventId, status) {
        try {
            const response = await this.makeRequest(`/om/pm-events/${eventId}/`, {
                method: 'PATCH',
                body: JSON.stringify({ status }),
            });
            return {
                success: true,
                event: response
            };
        } catch (error) {
            console.error('updatePMCalendarStatus error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Create PTW workflow package from a PM Calendar event
     * @param {string|number} eventId
     * @param {Object} payload
     */
    async connectPMTaskToPTWFlow(eventId, payload = {}) {
        try {
            const response = await this.makeRequest(`/om/pm-events/${eventId}/connect-ptw-flow/`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            return { success: true, ...(response || {}) };
        } catch (error) {
            console.error('connectPMTaskToPTWFlow error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Bulk-update status for multiple PM Calendar events
     * @param {Array<string|number>} ids
     * @param {string} status
     */
    async bulkUpdatePMCalendarStatus(ids, status) {
        try {
            const response = await this.makeRequest('/om/pm-events/bulk-status/', {
                method: 'PATCH',
                body: JSON.stringify({ ids, status }),
            });
            return { success: true, ...response };
        } catch (error) {
            console.error('bulkUpdatePMCalendarStatus error:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Get aggregated PM Calendar statistics for a site
     * @param {string} siteCode
     */
    async getPMCalendarStats(siteCode) {
        try {
            const endpoint = `/om/pm-events/stats/${siteCode ? `?site_code=${siteCode}` : ''}`;
            const response = await this.makeRequest(endpoint);
            return { success: true, stats: response };
        } catch (error) {
            console.error('getPMCalendarStats error:', error);
            return { success: false, error: error.message, stats: {} };
        }
    }

    /**
     * Get distinct equipment names from inventory for a given site.
     * Used by PM Calendar to populate the equipment dropdown.
     * @param {string} siteCode
     * @returns {{ success: boolean, equipment_names: string[] }}
     */
    async getInventoryEquipmentNames(siteCode) {
        try {
            const params = new URLSearchParams();
            if (siteCode) params.set('site_code', siteCode);
            const qs = params.toString();
            const endpoint = `/v1/inventory/equipment-names/${qs ? `?${qs}` : ''}`;
            const response = await this.makeRequest(endpoint);
            const names = response?.equipment_names || response?.data?.equipment_names || [];
            return { success: true, equipment_names: Array.isArray(names) ? names : [] };
        } catch (error) {
            console.error('getInventoryEquipmentNames error:', error);
            return { success: false, error: error.message, equipment_names: [] };
        }
    }

    async getSimulationTokenWallet(siteCode = null) {
        try {
            const params = new URLSearchParams();
            if (siteCode) params.append('site_code', siteCode);
            const endpoint = `/v1/fulfillment/token-wallet${params.toString() ? `?${params.toString()}` : ''}`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                ...response,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async rechargeSimulationTokens(userId, tokenAmount, remarks = '') {
        try {
            const response = await this.makeRequest('/admin/simulation-tokens/recharge/', {
                method: 'POST',
                body: JSON.stringify({
                    user_id: userId,
                    token_amount: tokenAmount,
                    remarks,
                }),
            });
            return {
                success: true,
                ...(response || {}),
                data: response?.data || null,
                message: response?.message || 'Token recharge completed.',
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    // ==================== ADMIN COMPANY TOKEN MANAGEMENT ====================

    async getAdminCompanyTokens(companyId) {
        try {
            const endpoint = `/v1/admin/company-tokens/?company_id=${companyId}`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                ...response,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getAdminCompanyTokenWallets() {
        try {
            const endpoint = `/v1/admin/company-tokens/list`;
            const response = await this.makeRequest(endpoint);
            return {
                success: true,
                ...response,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async adminAssignCompanyTokens(data) {
        try {
            const { company_id, user_id, tokens, remarks, tab_token_breakdown } = data;
            const response = await this.makeRequest('/v1/admin/company-tokens/assign', {
                method: 'POST',
                body: JSON.stringify({
                    company_id,
                    user_id,
                    tokens,
                    remarks,
                    tab_token_breakdown,
                }),
            });
            return {
                success: true,
                ...(response || {}),
                data: response?.data || null,
                message: response?.message || 'Tokens assigned successfully.',
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async adminBatchAssignTokens(data) {
        try {
            const { assignments } = data;
            const response = await this.makeRequest('/v1/admin/company-tokens/batch-assign', {
                method: 'POST',
                body: JSON.stringify({
                    assignments,
                }),
            });
            return {
                success: true,
                ...(response || {}),
                results: response?.results || { success: [], failed: [] },
                summary: response?.summary || { total: 0, success_count: 0, failed_count: 0 },
                message: response?.message || 'Batch assignment completed.',
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getNewsTicker() {
        try {
            const response = await this.makeRequest('/core/news/');
            return {
                success: true,
                data: response.data || [],
            };
        } catch (error) {
            return { success: false, error: error.message, data: [] };
        }
    }

    async getWeather(lat = 24.5854, lon = 73.7125) {
        try {
            const response = await this.makeRequest(`/core/weather/current/?lat=${lat}&lon=${lon}`);
            return {
                success: true,
                data: response.data || null,
            };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async getStockQuotes() {
        try {
            const response = await this.makeRequest('/core/stocks/');
            return {
                success: true,
                data: Array.isArray(response.data) ? response.data : [],
            };
        } catch (error) {
            return { success: false, error: error.message, data: [] };
        }
    }

    async getSiteAttendanceLogs(params = {}) {
        try {
            const query = new URLSearchParams(params).toString();
            const response = await this.makeRequest(`/v1/site-attendance/logs/?${query}`);
            return {
                success: true,
                count: response?.count || 0,
                logs: Array.isArray(response?.logs) ? response.logs : []
            };
        } catch (error) {
            return { success: false, error: error.message, logs: [] };
        }
    }

    async getFieldInspectionProofs(params = {}) {
        try {
            const query = new URLSearchParams(params).toString();
            const response = await this.makeRequest(`/v1/site-attendance/inspection-proofs/?${query}`);
            return {
                success: true,
                count: response?.count || 0,
                proofs: Array.isArray(response?.proofs) ? response.proofs : []
            };
        } catch (error) {
            return { success: false, error: error.message, proofs: [] };
        }
    }

    async checkPMEquipmentConflict(payload) {
        try {
            const res = await this.makeRequest('/om/pm-events/check-conflict/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return res;
        } catch (error) {
            return { has_conflict: false, conflicts: [], error: error.message };
        }
    }

    // ==========================================
    // Performance Analytics Chart Visibility API
    // ==========================================

    async getAdminChartConfigs({ siteCode = '', siteId = null, companyId = null, dashboardType = 'performance-analytics', signal } = {}) {
        try {
            const params = new URLSearchParams();
            if (siteCode) params.append('site_code', siteCode);
            if (siteId) params.append('site_id', String(siteId));
            if (companyId) params.append('company_id', String(companyId));
            if (dashboardType) params.append('dashboard_type', dashboardType);

            const queryString = params.toString();
            const endpoint = `/admin/chart-management/performance-analytics/${queryString ? `?${queryString}` : ''}`;
            const response = await this.makeRequest(endpoint, { signal });

            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || {},
                };
            }
            return {
                success: false,
                error: response?.message || response?.error || 'Failed to fetch chart configurations',
                data: null,
            };
        } catch (error) {
            return { success: false, error: error.message || 'Failed to fetch chart configurations', data: null };
        }
    }

    async saveAdminChartConfig(payload) {
        try {
            const response = await this.makeRequest('/admin/chart-management/performance-analytics/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to save chart configuration' };
        }
    }

    async saveAdminChartConfigs(payload) {
        return this.saveAdminChartConfig(payload);
    }

    async resetAdminChartConfig(siteCodeOrParams, dashboardType) {
        try {
            const siteCode = typeof siteCodeOrParams === 'object' ? siteCodeOrParams?.site_code : siteCodeOrParams;
            const targetDashType = typeof siteCodeOrParams === 'object' ? (siteCodeOrParams?.dashboard_type || dashboardType) : dashboardType;
            const params = new URLSearchParams();
            if (siteCode) params.append('site_code', siteCode);
            if (targetDashType) params.append('dashboard_type', targetDashType);

            const response = await this.makeRequest(`/admin/chart-management/performance-analytics/?${params.toString()}`, {
                method: 'DELETE',
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to reset chart configuration' };
        }
    }

    async resetAdminChartConfigs(params) {
        return this.resetAdminChartConfig(params);
    }

    async bulkCopyAdminChartConfig({ sourceSiteCode, targetSiteCodes, source_site_code, target_site_codes, dashboardType, dashboard_type }) {
        try {
            const response = await this.makeRequest('/admin/chart-management/bulk-copy/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source_site_code: source_site_code || sourceSiteCode,
                    target_site_codes: target_site_codes || targetSiteCodes,
                    dashboard_type: dashboard_type || dashboardType,
                }),
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to copy chart configurations' };
        }
    }

    async copySitePerformanceChartConfig(params) {
        return this.bulkCopyAdminChartConfig(params);
    }

    async getSitePerformanceChartConfig(siteCode, dashboardType = 'live-2') {
        if (!siteCode) return { success: false, data: null };
        try {
            const endpoint = `/dashboard/performance-analytics/charts-config/?site_code=${encodeURIComponent(siteCode)}&dashboard_type=${encodeURIComponent(dashboardType)}`;
            const response = await this.makeRequest(endpoint);
            if (response && response.success) {
                return {
                    success: true,
                    data: response.data || {},
                };
            }
            return { success: false, error: response?.message || 'Failed to load chart configuration', data: null };
        } catch (error) {
            return { success: false, error: error.message || 'Failed to load chart configuration', data: null };
        }
    }

    // Site Telemetry Database Mapping API
    async getSiteDatabaseMappings() {
        try {
            const response = await this.makeRequest('/admin/site-database-mappings/');
            if (response && response.success) {
                return {
                    success: true,
                    mappings: response.mappings || [],
                    total_count: response.total_count || 0
                };
            }
            return { success: false, error: response?.message || 'Failed to fetch database mappings', mappings: [] };
        } catch (error) {
            return { success: false, error: error.message || 'Failed to fetch database mappings', mappings: [] };
        }
    }

    async updateSiteDatabaseMapping(payload) {
        try {
            const response = await this.makeRequest('/admin/site-database-mappings/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to update database mapping' };
        }
    }

    async testDatabaseConnection(databaseName) {
        try {
            const response = await this.makeRequest('/admin/site-database-mappings/test/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ database_name: databaseName })
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to test database connection' };
        }
    }

    async bootstrapDatabaseTables(databaseName) {
        try {
            const response = await this.makeRequest('/admin/site-database-mappings/bootstrap/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ database_name: databaseName })
            });
            return response;
        } catch (error) {
            return { success: false, error: error.message || 'Failed to bootstrap database tables' };
        }
    }

    /**
     * Submit public or authenticated data deletion request
     */
    async submitDataDeletionRequest(payload) {
        try {
            return await this.makeRequest('/data-deletion-request/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to submit data deletion request.'
            };
        }
    }

    /**
     * Get paginated and filtered data deletion requests (Admin only)
     */
    async getDataDeletionRequests(params = {}) {
        try {
            const query = new URLSearchParams();
            if (params.page) query.append('page', params.page);
            if (params.page_size) query.append('page_size', params.page_size);
            if (params.status && params.status !== 'ALL') query.append('status', params.status);
            if (params.source && params.source !== 'ALL') query.append('source', params.source);
            if (params.search) query.append('search', params.search);

            const queryString = query.toString() ? `?${query.toString()}` : '';
            return await this.makeRequest(`/admin/data-deletion-requests/${queryString}`, {
                method: 'GET'
            });
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to load data deletion requests.',
                results: [],
                stats: { total: 0, pending: 0, in_review: 0, completed: 0, rejected: 0 }
            };
        }
    }

    /**
     * Get single data deletion request detail
     */
    async getDataDeletionRequestDetail(ticketId) {
        try {
            return await this.makeRequest(`/admin/data-deletion-requests/${encodeURIComponent(ticketId)}/`, {
                method: 'GET'
            });
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to retrieve deletion request detail.'
            };
        }
    }

    /**
     * Update data deletion request status and admin notes (Admin only)
     */
    async updateDataDeletionRequest(ticketId, payload) {
        try {
            return await this.makeRequest(`/admin/data-deletion-requests/${encodeURIComponent(ticketId)}/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to update deletion request.'
            };
        }
    }

    // MFA / Microsoft Authenticator methods
    async verifyLoginMFA(mfaToken, code) {
        try {
            const response = await this.makeRequest('/auth/mfa/verify-login/', {
                method: 'POST',
                body: JSON.stringify({ mfa_token: mfaToken, code }),
            });

            if (response.success && response.data) {
                const accessToken =
                    this.normalizeToken(response?.data?.access_token) ||
                    this.normalizeToken(response?.data?.token);

                if (accessToken) {
                    this.setToken(accessToken);
                    this.setRefreshToken(response?.data?.refresh_token);
                }

                return {
                    success: true,
                    token: accessToken,
                    user: response.data.user,
                    data: response.data,
                    message: response.message
                };
            }

            return {
                success: false,
                error: response.message || 'MFA verification failed'
            };
        } catch (error) {
            return {
                success: false,
                error: error.response?.message || error.message || 'MFA verification failed'
            };
        }
    }

    async getMFAStatus() {
        try {
            return await this.makeRequest('/auth/mfa/status/', { method: 'GET' });
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async setupMFA(regenerate = false) {
        try {
            return await this.makeRequest('/auth/mfa/setup/', {
                method: 'POST',
                body: JSON.stringify({ regenerate }),
            });
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async confirmMFASetup(code) {
        try {
            return await this.makeRequest('/auth/mfa/confirm-setup/', {
                method: 'POST',
                body: JSON.stringify({ code }),
            });
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    async disableMFA(password, code = '') {
        try {
            return await this.makeRequest('/auth/mfa/disable/', {
                method: 'POST',
                body: JSON.stringify({ password, code }),
            });
        } catch (error) {
            return { success: false, error: error.message };
        }
    }

    getCSRFToken() {

        // Extract CSRF token from cookies
        const name = 'csrftoken=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i].trim();
            if (cookie.indexOf(name) === 0) {
                return cookie.substring(name.length, cookie.length);
            }
        }
        return '';
    }
}

export default new BackendApiService();


